import { getProgressErrorMessage, isProgressError, PROGRESS_EVENT_TYPENAME } from "@ankaadia/ankaadia-shared";
import type { IProgressEvent } from "@ankaadia/ankaadia-shared";
import type { Observable } from "rxjs";
import { defer, filter, finalize, take, takeUntil, tap } from "rxjs";
import { ProgressDialogServiceBase } from "./progress-dialog.service";
import { ProgressErrorNotifierBase } from "./progress-error-notifier";
import { ProgressNotificationService } from "./progress-notification.service";
import type { ProgressMode } from "./progress.model";

export interface ReportProgressOptions {
  /** Suppress the modal dialog (default: false) */
  silent?: boolean;
  /** Show a cancel button in the dialog (default: true) */
  cancelable?: boolean;
  mode?: ProgressMode;
}

/**

 * forwarding progress updates to the {@link ProgressNotificationService} (and optionally
 * displaying a modal dialog via {@link ProgressDialogServiceBase}), while emitting only
 * non-progress results downstream.
 *
 * @param title   - Title shown in the progress dialog.
 * @param id      - Unique progress channel identifier (default `"1"`).
 * @param multi   - When `true`  the stream stays open for multiple results.
 *                  When `false` (default), completes after the first result (`take(1)`).
 * @param options - Optional settings:
 *   - `silent`     – Suppress the modal dialog (default `false`).
 *   - `cancelable` – Show a cancel button in the dialog (default `true`).
 *                     If cancelled, the pipeline is unsubscribed via `takeUntil`.
 */
export function reportProgress<TUnion extends { __typename?: string }>(
  title: string,
  id = "1",
  multi = false,
  options?: ReportProgressOptions
) {
  type TResult = Exclude<TUnion, { __typename?: "ProgressEvent" }>;

  const silent = options?.silent ?? false;
  const cancelable = options?.cancelable ?? false;
  const mode = options?.mode ?? "activity-indicator";

  return (source: Observable<TUnion>): Observable<TResult> => {
    return defer(() => {
      const dialogHandle =
        !silent && ProgressDialogServiceBase.instance
          ? ProgressDialogServiceBase.instance.open(title, id, { cancelable, mode })
          : null;

      let pipeline: Observable<TResult> = source.pipe(
        tap((progressEvent) => {
          if (progressEvent.__typename === PROGRESS_EVENT_TYPENAME) {
            const pe = progressEvent as unknown as IProgressEvent;
            if (isProgressError(pe)) {
              ProgressErrorNotifierBase.instance?.notifyError(pe);
              throw new Error(getProgressErrorMessage(pe));
            }
            if (ProgressNotificationService.instance) {
              ProgressNotificationService.instance.emitProgress({ ...progressEvent, id });
            } else {
              // eslint-disable-next-line no-console
              console.warn("ProgressNotificationService instance not found. Progress updates will not be displayed.");
            }
          }
        }),
        filter((event): event is TUnion & TResult => event.__typename !== PROGRESS_EVENT_TYPENAME),
        finalize(() => {
          if (!silent && ProgressDialogServiceBase.instance) {
            ProgressDialogServiceBase.instance.close();
          }
        })
      );

      if (dialogHandle) {
        pipeline = pipeline.pipe(takeUntil(dialogHandle.cancelled$));
      }

      return multi ? pipeline : pipeline.pipe(take(1));
    }) as Observable<TResult>;
  };
}
